import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCurrency } from '../../lib/utils';
import { 
  FileText, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Building,
  Users,
  CreditCard,
  PieChart
} from 'lucide-react';

const DashboardStats = ({ stats }) => {
  const completionRate = stats.totalProjects > 0 ? Math.round((stats.completedProjects / stats.totalProjects) * 100) : 0;
  const paymentProgress = stats.totalRevenue > 0 ? Math.round((stats.totalPaid / stats.totalRevenue) * 100) : 0;
  const profitMargin = stats.totalActualReceived > 0 ? Math.round((stats.netProfit / stats.totalActualReceived) * 100) : 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
      {/* Total Projects */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center space-x-2 text-sm">
            <FileText className="h-4 w-4" />
            <span>โปรเจคทั้งหมด</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="text-xl sm:text-2xl font-bold text-blue-600">{stats.totalProjects}</div>
          <div className="text-xs text-gray-600 mt-2 space-y-1">
            <div className="flex justify-between">
              <span>เสร็จ:</span>
              <span className="font-medium">{stats.completedProjects}</span>
            </div>
            <div className="flex justify-between">
              <span>กำลังทำ:</span>
              <span className="font-medium">{stats.inProgressProjects}</span>
            </div>
            <div className="flex justify-between">
              <span>รอ:</span>
              <span className="font-medium">{stats.pendingProjects}</span>
            </div>
          </div>
          <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${completionRate}%` }}
            ></div>
          </div>
          <div className="text-xs text-blue-600 mt-2 text-center font-medium">{completionRate}% เสร็จสิ้น</div>
        </CardContent>
      </Card>

      {/* Total Revenue */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center space-x-2 text-sm">
            <DollarSign className="h-4 w-4" />
            <span>รายได้รวม</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="text-xl sm:text-2xl font-bold text-green-600">{formatCurrency(stats.totalRevenue)}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="text-xs text-green-700 font-medium">ชำระแล้ว</div>
              <div className="text-sm font-bold text-green-800">{formatCurrency(stats.totalPaid)}</div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="text-xs text-red-700 font-medium">ค้างชำระ</div>
              <div className="text-sm font-bold text-red-800">{formatCurrency(stats.pendingAmount)}</div>
            </div>
          </div>
          <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${paymentProgress}%` }}
            ></div>
          </div>
          <div className="text-xs text-green-600 mt-2 text-center font-medium">{paymentProgress}% ชำระแล้ว</div>
        </CardContent>
      </Card>

      {/* Actual Received */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center space-x-2 text-sm">
            <TrendingUp className="h-4 w-4" />
            <span>รายได้จริง</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="text-xl sm:text-2xl font-bold text-purple-600">{formatCurrency(stats.totalActualReceived)}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <div className="text-xs text-purple-700 font-medium">หัก VAT</div>
              <div className="text-sm font-bold text-purple-800">{formatCurrency(stats.totalVatAmount)}</div>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="text-xs text-orange-700 font-medium">หัก WHT</div>
              <div className="text-sm font-bold text-orange-800">{formatCurrency(stats.totalWithholdingTax)}</div>
            </div>
          </div>
          <div className="text-xs text-purple-600 mt-3 text-center font-medium">
            หลังหักภาษีแล้ว
          </div>
        </CardContent>
      </Card>

      {/* Net Profit */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center space-x-2 text-sm">
            <TrendingDown className="h-4 w-4" />
            <span>กำไรสุทธิ</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className={`text-xl sm:text-2xl font-bold ${stats.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(stats.netProfit)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-xs text-blue-700 font-medium">รายได้จริงที่ชำระแล้ว</div>
              <div className="text-sm font-bold text-blue-800">{formatCurrency(stats.paidAmountAfterVatWht || 0)}</div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="text-xs text-red-700 font-medium">ค่าใช้จ่ายที่จ่ายแล้ว</div>
              <div className="text-sm font-bold text-red-800">{formatCurrency(stats.totalPaidExpenses)}</div>
            </div>
          </div>
          <div className={`text-xs mt-3 text-center font-medium ${profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {profitMargin >= 0 ? '+' : ''}{profitMargin}% กำไร
          </div>
        </CardContent>
      </Card>

      {/* Company Fee */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center space-x-2 text-sm">
            <Building className="h-4 w-4" />
            <span>ค่าบริการบริษัท</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="text-xl sm:text-2xl font-bold text-indigo-600">{formatCurrency(stats.totalCompanyFee)}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="text-xs text-green-700 font-medium">จ่ายแล้ว</div>
              <div className="text-sm font-bold text-green-800">{formatCurrency(stats.paidCompanyFee)}</div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="text-xs text-red-700 font-medium">ค้างจ่าย</div>
              <div className="text-sm font-bold text-red-800">{formatCurrency(stats.pendingCompanyFee)}</div>
            </div>
          </div>
          <div className="text-xs text-indigo-600 mt-3 text-center font-medium">
            รายได้บริษัท
          </div>
        </CardContent>
      </Card>

      {/* Developer Payments */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center space-x-2 text-sm">
            <Users className="h-4 w-4" />
            <span>ค่าจ้าง Developer</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="text-xl sm:text-2xl font-bold text-orange-600">{formatCurrency(stats.totalDeveloperPayments)}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="text-xs text-green-700 font-medium">จ่ายแล้ว</div>
              <div className="text-sm font-bold text-green-800">{formatCurrency(stats.paidDeveloperPayments)}</div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="text-xs text-red-700 font-medium">ค้างจ่าย</div>
              <div className="text-sm font-bold text-red-800">{formatCurrency(stats.pendingDeveloperPayments)}</div>
            </div>
          </div>
          <div className="text-xs text-orange-600 mt-3 text-center font-medium">
            ค่าใช้จ่ายหลัก
          </div>
        </CardContent>
      </Card>

      {/* Other Expenses */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center space-x-2 text-sm">
            <CreditCard className="h-4 w-4" />
            <span>ค่าใช้จ่ายโปรเจค</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="text-xl sm:text-2xl font-bold text-red-600">{formatCurrency(stats.totalOtherExpenses)}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="text-xs text-green-700 font-medium">จ่ายแล้ว</div>
              <div className="text-sm font-bold text-green-800">{formatCurrency(stats.paidOtherExpenses)}</div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="text-xs text-red-700 font-medium">ค้างจ่าย</div>
              <div className="text-sm font-bold text-red-800">{formatCurrency(stats.pendingOtherExpenses)}</div>
            </div>
          </div>
          <div className="text-xs text-red-600 mt-3 text-center font-medium">
            ค่าใช้จ่ายเสริม
          </div>
        </CardContent>
      </Card>

      {/* Sales Commission */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center space-x-2 text-sm">
            <PieChart className="h-4 w-4" />
            <span>Commission Sale</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-cyan-600">{formatCurrency(stats.totalSalesCommission)}</div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="p-2 bg-green-50 rounded-lg border border-green-200">
              <div className="text-xs text-green-700 font-medium">จ่ายแล้ว</div>
              <div className="text-sm font-bold text-green-800">{formatCurrency(stats.paidSalesCommission)}</div>
            </div>
            <div className="p-2 bg-red-50 rounded-lg border border-red-200">
              <div className="text-xs text-red-700 font-medium">ค้างจ่าย</div>
              <div className="text-sm font-bold text-red-800">{formatCurrency(stats.pendingSalesCommission)}</div>
            </div>
          </div>
          <div className="text-xs text-cyan-600 mt-2 text-center">
            ค่าคอมมิชชั่น
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardStats;
